import type { VoiceSettings } from "@ma-soi/game-core";
import {
  ArrowRight,
  BookOpenText,
  ChevronRight,
  DoorOpen,
  LockKeyhole,
  Moon,
  Music2,
  Plus,
  ShieldCheck,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GameButton, SettingsToggle } from "../components/GameUi";
import { AnimatedContent } from "../components/ui/AnimatedContent";
import { useVisualEffects } from "../hooks/VisualEffectsContext";
import { NeutralAvatarStack } from "../components/SeatAvatar";
import { useGlobalSound } from "../hooks/SoundContext";
import { AUDIO_CREDITS } from "../audio/catalog";
import { apiRequest, errorMessage, publicRequest, requestId, saveResumeToken } from "../lib";

interface PublicRoom {
  roomId: string;
  title: string;
  hostName: string;
  playerCount: number;
  maxPlayers: number;
  discussionSeconds: number;
}

type SheetName = "create" | "join" | "profile" | "rules" | "credits" | null;

const MIN_PLAYERS = 6;
const MAX_PLAYERS = 16;
const SPECIAL_ROLES = [
  ["", "Đầy đủ vai đặc biệt"],
  ["SEER", "Không có Tiên tri"],
  ["WITCH", "Không có Phù thủy"],
  ["HUNTER", "Không có Thợ săn"],
  ["GUARD", "Không có Bảo vệ"],
] as const;

function wait(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

function AppSheet({
  title,
  eyebrow,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    // Dialog native giữ focus bên trong và làm nền phía sau không thể tương tác.
    dialog?.showModal();
    (dialog?.querySelector<HTMLElement>("[data-initial-focus]") ?? dialog?.querySelector<HTMLElement>("input, select, button") ?? dialog)?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  return (
    <dialog ref={dialogRef} className={`app-sheet ${wide ? "app-sheet-wide" : ""}`} aria-labelledby="sheet-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onKeyDown={(event) => {
        // Quấn Tab trong form, không để trình duyệt chuyển focus sang thanh địa chỉ.
        if (event.key !== "Tab") return;
        const controls = [...event.currentTarget.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href], [tabindex='0']")];
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}
      onClick={(event) => {
        // Chỉ đóng khi bấm thật sự ngoài khung, không đóng khi thao tác nội dung.
        const box = event.currentTarget.getBoundingClientRect();
        if (event.target === event.currentTarget && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) onClose();
      }}>
      <header className="app-sheet-header">
        <div>
          <p className="cinematic-eyebrow">{eyebrow}</p>
          <h2 id="sheet-title">{title}</h2>
        </div>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Đóng"><X /></button>
      </header>
      <div className="app-sheet-body">{children}</div>
    </dialog>
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field-label"><span>{label}</span>{children}</label>;
}

export function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const sound = useGlobalSound();
  const effects = useVisualEffects();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const [sheet, setSheet] = useState<SheetName>(null);
  const [name, setName] = useState(() => localStorage.getItem("ma-soi:name") ?? "");
  const [roomCode, setRoomCode] = useState("");
  const [title, setTitle] = useState("Ngôi làng vô danh");
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [visibility, setVisibility] = useState<"public" | "private">("private");
  const [discussionSeconds, setDiscussionSeconds] = useState(180);
  const [votingSeconds, setVotingSeconds] = useState(60);
  const [nightActionSeconds, setNightActionSeconds] = useState(45);
  const [disabledSpecialRole, setDisabledSpecialRole] = useState("");
  const [revealRoleOnDeath, setRevealRoleOnDeath] = useState(false);
  const [ghostChatEnabled, setGhostChatEnabled] = useState(true);
  const [guardRepeatTarget, setGuardRepeatTarget] = useState(false);
  const [voiceChatEnabled, setVoiceChatEnabled] = useState(true);
  const [werewolfVoiceEnabled, setWerewolfVoiceEnabled] = useState(true);
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [roomsStatus, setRoomsStatus] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    sound.setScene("home", "home");
  }, [sound.setScene]);

  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      try {
        const result = await publicRequest<{ rooms: PublicRoom[] }>("/api/public-rooms");
        if (!disposed) { setRooms(Array.isArray(result.rooms) ? result.rooms : []); setRoomsStatus("ready"); }
      } catch {
        if (!disposed) setRoomsStatus("error");
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 8_000);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      localStorage.setItem("ma-soi:name", name);
      const voiceSettings: Partial<VoiceSettings> = {
        enabled: voiceChatEnabled,
        lobbyVoice: voiceChatEnabled,
        dayVoice: voiceChatEnabled,
        werewolfNightVoice: voiceChatEnabled && werewolfVoiceEnabled,
        deadCanListen: true,
        ghostVoiceChat: ghostChatEnabled,
        muteDuringVoting: true,
        pushToTalk: false,
        voiceActivityDetection: true,
        duckBackgroundMusic: true,
        mutedAll: false,
        listenOnlyMode: false,
      };
      const result = await apiRequest<{ roomId: string; resumeToken: string }>("/api/rooms", {
        method: "POST",
        body: JSON.stringify({
          requestId: requestId(),
          name,
          title,
          visibility,
          maxPlayers,
          discussionSeconds,
          votingSeconds,
          nightActionSeconds,
          revealRoleOnDeath,
          ghostChatEnabled,
          guardRepeatTarget,
          disabledSpecialRole: disabledSpecialRole || null,
          voiceSettings,
        }),
      });
      saveResumeToken(result.roomId, result.resumeToken);
      sound.playSfx("room_create");
      setTransitioning(true);
      if (!effects.reduced) await wait(200);
      navigate(`/room/${result.roomId}`);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  async function joinRoom(code = roomCode) {
    setBusy(true);
    setError(null);
    try {
      const normalizedCode = code.trim().toUpperCase();
      localStorage.setItem("ma-soi:name", name);
      const result = await apiRequest<{ roomId: string; resumeToken: string }>(`/api/rooms/${normalizedCode}/join`, {
        method: "POST",
        body: JSON.stringify({ requestId: requestId(), name }),
      });
      saveResumeToken(result.roomId, result.resumeToken);
      sound.playSfx("room_join_success");
      setTransitioning(true);
      if (!effects.reduced) await wait(200);
      navigate(`/room/${result.roomId}`);
    } catch (reason) {
      setError(errorMessage(reason));
      sound.playSfx("room_join_failure");
    } finally {
      setBusy(false);
    }
  }

  async function join(event: FormEvent) {
    event.preventDefault();
    await joinRoom();
  }

  function openPublicRoom(room: PublicRoom) {
    setRoomCode(room.roomId);
    if (name.trim().length >= 2) void joinRoom(room.roomId);
    else setSheet("join");
  }

  return (
    <div className={`landing-page ${transitioning ? "mist-transition" : ""}`}>
      <main className="home-dashboard">
        {notice && <p className="notice-box" role="status">{notice}</p>}
        {error && !sheet && <p className="error-box" role="alert">{error}</p>}
        <section className="home-hero" aria-labelledby="home-title">
          <div className="home-hero-copy">
            <p className="cinematic-eyebrow">Nghi thức dưới trăng máu</p>
            <h1 id="home-title">MA SÓI<span>ĐÊM TRĂNG MÁU</span></h1>
            <p>Đêm xuống, ngôi làng khép cửa.<br />Giữa những gương mặt quen, ai đang giấu nanh?</p>
          </div>
          <AnimatedContent className="home-console gothic-frame" duration={0.24} distance={8} scale={1} initialOpacity={1}>
            <p className="cinematic-eyebrow">Hội đồng đang chờ</p>
            <h2>Bước vào ngôi làng</h2>
            <p className="home-console-intro">Tụ họp bạn bè. Nhận lá bài. Giữ kín số phận.</p>
            <div className="home-primary-actions">
              <GameButton fullWidth icon={<Plus />} disabled={busy} onClick={() => { setError(null); setSheet("create"); }}>Tạo phòng</GameButton>
              <GameButton fullWidth variant="secondary" icon={<DoorOpen />} disabled={busy} onClick={() => { setError(null); setSheet("join"); }}>Nhập mã</GameButton>
            </div>
            <div className="home-rule" />
            <a className="home-public-link" href="#public-rooms"><UsersRound />Phòng công khai</a>
            <div className="home-secondary-actions">
              <button type="button" className="home-rules-button" onClick={() => setSheet("rules")}><BookOpenText />Luật chơi</button>
              <button type="button" className="home-rules-button" onClick={() => setSheet("profile")} aria-label="Hồ sơ người chơi"><UserRound /><span>{name || "Hồ sơ"}</span></button>
            </div>
          </AnimatedContent>
        </section>
        <section className="home-features" aria-label="Thông tin trò chơi">
          <div><UsersRound /><p><strong>6–16 người</strong><span>Một ngôi làng, nhiều bí mật</span></p></div>
          <div><LockKeyhole /><p><strong>Vai trò bí mật</strong><span>Sáu lá bài định mệnh</span></p></div>
          <div><Moon /><p><strong>Quản trò tự động</strong><span>Nhịp đêm và ngày liền mạch</span></p></div>
        </section>
        <section className="public-room-section" aria-labelledby="public-rooms">
          <header><div><p className="cinematic-eyebrow">Đang mở cửa</p><h2 id="public-rooms" tabIndex={-1}>Phòng công khai</h2></div></header>
          <div className="public-room-list" aria-busy={roomsStatus === "loading"}>
            {roomsStatus === "error" && <p className="notice-box" role="status">Chưa tải được danh sách phòng. Đang thử kết nối lại…</p>}
            {rooms.length === 0 ? (
              <div className="public-room-empty" role="status">
                <UsersRound />
                <strong>{roomsStatus === "loading" ? "Đang tìm ngôi làng…" : roomsStatus === "error" ? "Danh sách tạm thời chưa sẵn sàng" : "Chưa có phòng công khai"}</strong>
                <small>{roomsStatus === "ready" ? "Bạn có thể tạo một ngôi làng và mời bạn bè." : "Bạn vẫn có thể tạo phòng hoặc nhập mã đã biết."}</small>
              </div>
            ) : rooms.map((room) => (
              <button key={room.roomId} type="button" className="public-room-row" disabled={busy} onClick={() => openPublicRoom(room)}>
                <span className="public-room-name"><strong>{room.title}</strong><small>Chủ phòng: {room.hostName}</small></span>
                <NeutralAvatarStack count={room.playerCount} />
                <span className="public-room-count">{room.playerCount}/{room.maxPlayers}</span><ChevronRight />
              </button>
            ))}
          </div>
        </section>
        <footer className="home-footer"><span>Ma Sói · Đêm Trăng Máu</span><button type="button" className="home-rules-button" onClick={() => setSheet("credits")}><Music2 />Âm thanh & giấy phép</button></footer>
      </main>

      {sheet === "profile" && (
        <AppSheet title="Hồ sơ người chơi" eyebrow="Danh tính" onClose={() => setSheet(null)}>
          <form className="room-form" onSubmit={(event) => {
            event.preventDefault();
            localStorage.setItem("ma-soi:name", name);
            setSheet(null);
          }}>
            <FormField label="Tên của bạn">
              <input className="field-input" value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={20} required data-initial-focus />
            </FormField>
            <GameButton type="submit" fullWidth icon={<ShieldCheck />}>Lưu tên</GameButton>
          </form>
        </AppSheet>
      )}

      {sheet === "join" && (
        <AppSheet title="Nhập mã phòng" eyebrow="Bước vào ngôi làng" onClose={() => setSheet(null)}>
          <form className="room-form" onSubmit={join}>
            <FormField label="Tên của bạn">
              <input className="field-input" value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={20} required />
            </FormField>
            <FormField label="Mã phòng">
              <input className="field-input room-code-input" value={roomCode} onChange={(event) => setRoomCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} minLength={6} maxLength={6} required data-initial-focus />
            </FormField>
            {error && <p className="error-box" role="alert">{error}</p>}
            <GameButton type="submit" fullWidth disabled={busy} icon={<DoorOpen />} trailingIcon={<ArrowRight />}>
              {busy ? "Đang vào phòng…" : "Vào phòng"}
            </GameButton>
          </form>
        </AppSheet>
      )}

      {sheet === "create" && (
        <AppSheet title="Tạo phòng mới" eyebrow="Thiết lập ngôi làng" onClose={() => setSheet(null)} wide>
          <form className="room-form create-room-form" onSubmit={create}>
            <div className="form-grid form-grid-two">
              <FormField label="Tên của bạn"><input className="field-input" value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={20} required /></FormField>
              <FormField label="Tên phòng"><input className="field-input" value={title} onChange={(event) => setTitle(event.target.value)} minLength={2} maxLength={50} required /></FormField>
              <FormField label="Quyền riêng tư">
                <select className="field-input" value={visibility} onChange={(event) => setVisibility(event.target.value as "public" | "private")}>
                  <option value="private">Phòng riêng</option><option value="public">Phòng công khai</option>
                </select>
              </FormField>
              <FormField label="Sức chứa">
                <select className="field-input" value={maxPlayers} onChange={(event) => setMaxPlayers(Number(event.target.value))}>
                  {Array.from({ length: MAX_PLAYERS - MIN_PLAYERS + 1 }, (_, index) => index + MIN_PLAYERS).map((count) => <option key={count} value={count}>{count} người</option>)}
                </select>
              </FormField>
              <FormField label="Thảo luận"><select className="field-input" value={discussionSeconds} onChange={(event) => setDiscussionSeconds(Number(event.target.value))}>{[60,120,180,300,600].map((seconds) => <option key={seconds} value={seconds}>{seconds / 60} phút</option>)}</select></FormField>
              <FormField label="Bỏ phiếu"><select className="field-input" value={votingSeconds} onChange={(event) => setVotingSeconds(Number(event.target.value))}>{[30,45,60,90,120].map((seconds) => <option key={seconds} value={seconds}>{seconds} giây</option>)}</select></FormField>
              <FormField label="Hành động đêm"><select className="field-input" value={nightActionSeconds} onChange={(event) => setNightActionSeconds(Number(event.target.value))}>{[30,45,60,75,90].map((seconds) => <option key={seconds} value={seconds}>{seconds} giây</option>)}</select></FormField>
              <FormField label="Bộ vai"><select className="field-input" value={disabledSpecialRole} onChange={(event) => setDisabledSpecialRole(event.target.value)}>{SPECIAL_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></FormField>
            </div>

            <div className="settings-rune-grid">
              <SettingsToggle checked={voiceChatEnabled} onChange={setVoiceChatEnabled} title="Voice chat" description="Mở kênh nói trong sảnh và ban ngày." />
              <SettingsToggle checked={werewolfVoiceEnabled} onChange={setWerewolfVoiceEnabled} disabled={!voiceChatEnabled} title="Kênh Ma Sói" description="Bầy Sói có kênh riêng trong đêm." />
              <SettingsToggle checked={revealRoleOnDeath} onChange={setRevealRoleOnDeath} title="Lộ vai khi chết" description="Công bố vai khi một người rời cuộc chơi." />
              <SettingsToggle checked={ghostChatEnabled} onChange={setGhostChatEnabled} title="Chat người chết" description="Người chết có kênh trò chuyện riêng." />
              <SettingsToggle checked={guardRepeatTarget} onChange={setGuardRepeatTarget} title="Bảo vệ liên tiếp" description="Cho phép che chở cùng mục tiêu nhiều đêm." />
            </div>
            {error && <p className="error-box" role="alert">{error}</p>}
            <GameButton type="submit" fullWidth dramatic disabled={busy} icon={<Plus />} trailingIcon={<ArrowRight />}>
              {busy ? "Đang tạo phòng…" : "Tạo phòng"}
            </GameButton>
          </form>
        </AppSheet>
      )}

      {sheet === "rules" && (
        <AppSheet title="Luật chơi nhanh" eyebrow="Cách chơi" onClose={() => setSheet(null)}>
          <div className="rules-list">
            <article><strong>1. Nhận vai</strong><p>Mỗi người nhận một vai bí mật khi chủ phòng bắt đầu ván.</p></article>
            <article><strong>2. Đêm xuống</strong><p>Các vai đặc biệt hành động, Ma Sói lựa chọn mục tiêu.</p></article>
            <article><strong>3. Thảo luận</strong><p>Dân làng đối chiếu lời khai và tìm người đáng ngờ.</p></article>
            <article><strong>4. Bỏ phiếu</strong><p>Người có nhiều phiếu nhất rời cuộc chơi. Hai phe tiếp tục cho đến khi có bên chiến thắng.</p></article>
          </div>
        </AppSheet>
      )}

      {sheet === "credits" && (
        <AppSheet title="Âm thanh & giấy phép" eyebrow="Ghi công" onClose={() => setSheet(null)}>
          <div className="rules-list audio-credit-list">
            <p className="audio-credit-intro">
              Toàn bộ âm thanh là tệp thu sẵn từ bên thứ ba; game không tự tổng hợp hoặc tạo âm thanh bằng AI.
            </p>
            {AUDIO_CREDITS.map((credit) => (
              <article key={`${credit.creator}:${credit.title}`}>
                <strong>{credit.title}</strong>
                <p>{credit.creator} · <a href={credit.licenseUrl ?? credit.sourceUrl} target="_blank" rel="noreferrer">{credit.license}</a></p>
                <a href={credit.sourceUrl} target="_blank" rel="noreferrer">Trang nguồn</a>
              </article>
            ))}
          </div>
        </AppSheet>
      )}
    </div>
  );
}
